import { useMemo, useState } from 'react';
import { TeContainerSlim } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import PageLayout from '../../components/Page/PageLayout';
import EmptyState from '../../components/Page/EmptyState';
import MigrationCandidateParentRow from '../../components/Migration/MigrationCandidateParentRow';
import RemoveSuggestionModal from './RemoveSuggestionModal';
import ReviewOrganizationModal from './ReviewOrganizationModal';
import AddOrganizationFromMigrationCandidateModal from './AddOrganizationFromMigrationCandidateModal';
import { useMigrationCandidateBusinessPartners } from '../../hooks/queries/migration.queries';
import { MigrationCandidateBusinessPartnerBody } from '../../api/generated/pm';

function PendingMerges() {
  const { t } = useTranslation('companies', { keyPrefix: 'pendingMerges' });
  const [searchValue, setSearchValue] = useState('');
  const [removeSuggestionTarget, setRemoveSuggestionTarget] =
    useState<MigrationCandidateBusinessPartnerBody | null>(null);
  const [reviewTarget, setReviewTarget] = useState<MigrationCandidateBusinessPartnerBody | null>(
    null
  );
  const [addAsNewOrgTarget, setAddAsNewOrgTarget] =
    useState<MigrationCandidateBusinessPartnerBody | null>(null);

  const { data, isLoading } = useMigrationCandidateBusinessPartners();

  const filteredItems = useMemo(() => {
    const items = data?.items ?? [];
    if (!searchValue.trim()) return items;
    const query = searchValue.toLowerCase();
    return items.filter((item) => item.name.toLowerCase().includes(query));
  }, [data?.items, searchValue]);

  const itemsCount = filteredItems.length;
  const showPlaceholder = isLoading || itemsCount === 0;

  return (
    <PageLayout
      title={t('title')}
      itemCount={itemsCount}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchPlaceholder={t('searchPlaceholder')}
    >
      {showPlaceholder ? (
        <EmptyState
          loading={isLoading}
          icon="business"
          text={isLoading ? t('loadingOrganizations') : t('noOrganizations')}
          subtext={t('noOrganizationsDescription')}
        />
      ) : (
        <TeContainerSlim column fill gap="s" overflow="auto">
          {filteredItems.map((candidate) => (
            <MigrationCandidateParentRow
              key={candidate.id}
              candidate={candidate}
              onReview={setReviewTarget}
              onRemove={setRemoveSuggestionTarget}
            />
          ))}
        </TeContainerSlim>
      )}
      <RemoveSuggestionModal
        organization={removeSuggestionTarget}
        open={removeSuggestionTarget !== null}
        onClose={() => setRemoveSuggestionTarget(null)}
      />
      <ReviewOrganizationModal
        organization={reviewTarget}
        open={reviewTarget !== null}
        onClose={() => setReviewTarget(null)}
        onAddAsNewOrganization={(organization) => {
          setReviewTarget(null);
          setAddAsNewOrgTarget(organization);
        }}
      />
      <AddOrganizationFromMigrationCandidateModal
        organization={addAsNewOrgTarget}
        open={addAsNewOrgTarget !== null}
        onClose={() => setAddAsNewOrgTarget(null)}
      />
    </PageLayout>
  );
}

export default PendingMerges;
